import React, { useState, useRef, useEffect } from "react";
import { formatNumber } from "shared/utils/numberFormatter";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { makeFormatValue, getThresholdValues } from "features/analytics/studio/utils/axisFormat";
import { formatDurationSeconds } from "features/analytics/studio/utils/durationFormat";
import { renderStepKindChip } from "features/analytics/studio/utils/stepKindChip";

const DEFAULT_STEP_COLOR = theme.primaryLight;

/** Convert #RRGGBB to rgba for shadows / translucent fills */
function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string") return `rgba(75, 87, 170, ${alpha})`;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  }
  if (h.length !== 6) return `rgba(75, 87, 170, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return `rgba(75, 87, 170, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function FunnelVisualization({ 
  steps = [], 
  measuring = 'conversion_rate',
  overallConversionRate = null,
  breakdowns = [], // New prop for breakdown data
  config,          // Chart / axis configuration (from editor)
  /** Pixel height of the funnel plot area (board cards pass a constrained value). */
  chartHeight,
  /** Tighter spacing and type for board embedding */
  boardCompact = false,
  /** Board row resize — scales funnel labels */
  contentScale = 1,
  /** Builder only: interactive tooltip + “See users” drill-down */
  enableStepUserDrilldown = false,
  onRequestStepUsers = null,
}) {
  const fs = (px) => Math.max(8, Math.round(px * (typeof contentScale === "number" && Number.isFinite(contentScale) ? contentScale : 1)));
  const [hoveredLightArea, setHoveredLightArea] = useState(null); // { stepIndex, x, y }
  const [hoveredDarkArea, setHoveredDarkArea] = useState(null);  // { stepIndex, x, y }
  const [hoveredSegment, setHoveredSegment] = useState(null); // { stepIndex, breakdownIndex, kind: 'light' | 'dark', x, y }
  const stepRefs = useRef({});
  const darkAreaRefs = useRef({});
  const segmentRefs = useRef({});
  const hoverClearTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
    },
    []
  );

  const cancelScheduledHoverClear = () => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
  };

  const scheduleClearHover = (setter) => {
    cancelScheduledHoverClear();
    hoverClearTimerRef.current = setTimeout(() => {
      hoverClearTimerRef.current = null;
      setter(null);
    }, 200);
  };

  const drilldown = enableStepUserDrilldown && typeof onRequestStepUsers === "function";
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const funnelAxis = config?.funnelAxis ?? "left"; // "left" | "right" - which side the Y-axis is on
  /** Board: narrower tick column + smaller plot inset so bars sit closer to the axis (less dead space on the left). */
  const axisColClass = boardCompact ? "w-10" : "w-12";
  /** Inset axis ticks from card edge so labels are not flush to the border (matches ~8px). */
  const axisEdgeClass = funnelAxis === "right" ? "right-2 pl-2" : "left-2 pr-2";
  const gridInsetClass =
    funnelAxis === "right"
      ? boardCompact
        ? "left-0 right-12"
        : "left-0 right-14"
      : boardCompact
        ? "left-12 right-0"
        : "left-14 right-0";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  /** Funnel UI: always one decimal for percentage strings (avoids 4dp tooltips / clutter). */
  const formatPercent = (value) => `${(Number(value) || 0).toFixed(1)}%`;
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYLabel = yAxisConfig.label;
  const chartAxisSideMargin = (() => {
    if (!leftYVisible) return {};
    const noLbl = boardCompact ? "48px" : "56px";
    const withLbl = boardCompact ? "80px" : "104px";
    const m = leftYLabel ? withLbl : noLbl;
    return funnelAxis === "right" ? { marginRight: m } : { marginLeft: m };
  })();
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";
  const outOfBounds = yAxisConfig.outOfBounds ?? "hidden"; // "hidden" = use threshold bounds (cropped view); "visible" = show entire chart, no bounds
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(yAxisConfig);
  const hasThresholds = minThreshold != null || maxThreshold != null;
  // Apply threshold bounds when Out of Bounds = Hidden. When Visible, show full chart.
  const effectiveHasThresholds = hasThresholds && outOfBounds === "hidden";
  const rangeMin = minThreshold ?? 0;
  const rangeMax = maxThreshold ?? 100;
  const rangeSpan = rangeMax - rangeMin;
  // Explicit: only "log" string enables logarithmic Y-axis; everything else is linear
  const yScale = (yAxisConfig && String(yAxisConfig.scale) === "log") ? "log" : "linear";
  const stepColors = config?.colors && Array.isArray(config.colors) ? config.colors : [];
  /** One accent for every step (first palette swatch / primary), not different colors per step. */
  const funnelBarColor =
    stepColors.length > 0 ? stepColors[0] || DEFAULT_STEP_COLOR : DEFAULT_STEP_COLOR;

  // Log scale: base-10; cannot show 0, use 0.1% as floor. Axis domain for ticks and bar mapping.
  const LOG_FLOOR_PCT = 0.1;
  const axisMinLog = yScale === "log" ? (effectiveHasThresholds ? Math.max(LOG_FLOOR_PCT, rangeMin) : LOG_FLOOR_PCT) : 0;
  const axisMaxLog = yScale === "log" ? (effectiveHasThresholds ? rangeMax : 100) : 100;
  const logSpan = axisMaxLog > axisMinLog && axisMinLog > 0 ? Math.log10(axisMaxLog) - Math.log10(axisMinLog) : 1;

  /** Map actual % value to display height % for log scale. Linear scale uses raw %. */
  const valueToHeightPct = (valuePct) => {
    if (yScale !== "log" || valuePct <= 0) return valuePct;
    const v = Math.max(valuePct, axisMinLog);
    return ((Math.log10(v) - Math.log10(axisMinLog)) / logSpan) * 100;
  };

  // Helper to format time interval
  const formatTimeInterval = (interval) => {
    if (interval == null && interval !== 0) return "—";
    if (typeof interval === "number" && !Number.isNaN(interval)) {
      return formatDurationSeconds(interval);
    }
    if (typeof interval === 'string') return interval;
    if (typeof interval === 'object' && interval !== null) {
      const { hours = 0, minutes = 0, seconds = 0 } = interval;
      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 && parts.length === 0) parts.push(`${seconds}s`);
      return parts.length > 0 ? parts.join(' ') : '0s';
    }
    return String(interval);
  };

  // Check if we have breakdowns
  const hasBreakdowns = breakdowns && breakdowns.length > 0;

  // Generate colors for breakdown segments
  const getBreakdownColors = () => {
    const colors = [
      theme.primaryLight,
      "var(--feedback-info)",
      "#10b981",
      "var(--feedback-warning)",
      "var(--feedback-error)",
      "#8b5cf6",
      "#06b6d4",
      "#84cc16",
      "#f97316",
      "#ec4899",
      "#6366f1",
      "#14b8a6",
      "#a855f7",
      "#eab308",
      "var(--feedback-success)",
    ];
    return colors;
  };

  const breakdownColors = getBreakdownColors();

  // Get first step value for scaling (all heights relative to this)
  // For conversion_rate, use count; for others, use value
  const getFirstStepValue = () => {
    if (measuring === "conversion_rate" || measuring === "time_to_convert") {
      return steps.length > 0 ? (steps[0]?.count || 0) : 100;
    }
    return steps.length > 0 ? (steps[0]?.value ?? steps[0]?.count ?? 0) : 100;
  };
  
  const firstStepValue = getFirstStepValue();

  // Get max value across all breakdowns for step 1 (for scaling)
  const getMaxBreakdownStep1Value = () => {
    if (!hasBreakdowns) return firstStepValue;
    
    let maxValue = 0;
    breakdowns.forEach(breakdown => {
      const step1Data = breakdown.steps?.step1;
      if (step1Data) {
        const value =
          measuring === "conversion_rate" || measuring === "time_to_convert"
            ? step1Data.count || 0
            : step1Data.value ?? step1Data.count ?? 0;
        maxValue = Math.max(maxValue, value);
      }
    });
    
    return maxValue > 0 ? maxValue : firstStepValue;
  };

  const maxBreakdownStep1Value = getMaxBreakdownStep1Value();

  // Calculate heights for a breakdown segment at a specific step
  const getBreakdownHeights = (breakdown, stepIndex) => {
    const getStepValue = (stepData) => {
      if (measuring === "conversion_rate" || measuring === "time_to_convert") {
        return stepData?.count || 0;
      }
      return stepData?.value != null && stepData?.value !== ""
        ? stepData.value
        : stepData?.count || 0;
    };

    const stepNum = stepIndex + 1;
    const currentStepData = breakdown.steps?.[`step${stepNum}`] || {};
    const currentValue = getStepValue(currentStepData);
    
    let prevValue = 0;
    if (stepIndex === 0) {
      // For step 1, use the step1 count
      prevValue = getStepValue(breakdown.steps?.step1 || {});
    } else {
      // For subsequent steps, use previous step's value
      const prevStepData = breakdown.steps?.[`step${stepIndex}`] || {};
      prevValue = getStepValue(prevStepData);
    }

    // Scale heights relative to max breakdown step1 value
    const lightHeight = maxBreakdownStep1Value > 0 
      ? (prevValue / maxBreakdownStep1Value) * 100 
      : 0;
    
    const darkHeight = maxBreakdownStep1Value > 0 
      ? (currentValue / maxBreakdownStep1Value) * 100 
      : 0;

    return {
      lightHeight: Math.min(lightHeight, 100),
      darkHeight: Math.min(darkHeight, 100),
      prevValue,
      currentValue,
      conversionRate: currentStepData.conversionRate || (stepIndex === 0 ? 100 : 0)
    };
  };

  // Calculate funnel step heights (conversion-based)
  const getHeights = (step, index) => {
    const getStepValue = (s) => {
      if (measuring === "conversion_rate" || measuring === "time_to_convert") {
        return s.count || 0;
      }
      return s.value != null && s.value !== "" ? s.value : s.count || 0;
    };
    
    const currentValue = getStepValue(step);
    const prevValue = index === 0 
      ? firstStepValue 
      : getStepValue(steps[index - 1]);
    
    // Light background = previous step value (scaled to first step)
    const lightHeight = firstStepValue > 0 
      ? (prevValue / firstStepValue) * 100 
      : 0;
    
    // Dark fill = current step value (scaled to first step)
    const darkHeight = firstStepValue > 0 
      ? (currentValue / firstStepValue) * 100 
      : 0;
    
    return {
      lightHeight: Math.min(lightHeight, 100),
      darkHeight: Math.min(darkHeight, 100),
      prevValue,
      currentValue
    };
  };

  // Get display value for step (badge shows step-to-step conversion)
  const getDisplayValue = (step, measuring) => {
    const count = step.count || 0;
    const conversion = step.conversion || 0; // Step-to-step conversion from backend
    const fmt = formatYValue;
    switch (measuring) {
      case 'conversion_rate':
        // Counts are users at the step; axis unit (often %) must not format these as percentages.
        return { percentage: formatPercent(conversion), count: formatNumber(count) };
      case 'unique_users':
        return { percentage: formatPercent(conversion), count: formatNumber(step.value || count) };
      case 'total_conversions':
        return { percentage: formatPercent(conversion), count: formatNumber(step.value || 0) };
      case 'total_sessions':
        return { percentage: formatPercent(conversion), count: formatNumber(step.value || 0) };
      case "time_to_convert": {
        const stepNum = step.step ?? 1;
        const isLast = steps.length > 0 && stepNum === steps.length;
        let timeStr = "—";
        if (isLast) {
          if (step.timeToConvert) {
            timeStr = String(step.timeToConvert);
          } else if (typeof step.value === "number" && !Number.isNaN(step.value)) {
            timeStr = formatDurationSeconds(step.value);
          }
        }
        return {
          percentage: timeStr,
          count: formatPercent(conversion),
        };
      }
      default:
        return { percentage: `${conversion.toFixed(1)}%`, count: formatNumber(count) };
    }
  };

  // Tooltip for breakdown segment (converted)
  const getBreakdownTooltip = (breakdown, stepIndex) => {
    const stepNum = stepIndex + 1;
    const stepData = breakdown.steps?.[`step${stepNum}`] || {};
    const count = stepData.count || 0;
    const conversionRate = stepData.conversionRate || (stepIndex === 0 ? 100 : 0);
    const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';

    return [
      breakdownName,
      `${conversionRate.toFixed(1)}% converted`,
      `${formatNumber(count)} total times`
    ];
  };

  // Tooltip for breakdown segment drop-off (light area)
  const getBreakdownDropoffTooltip = (breakdown, stepIndex) => {
    const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';
    const stepNum = stepIndex + 1;

    const current = breakdown.steps?.[`step${stepNum}`]?.count || 0;
    let prev;

    if (stepIndex === 0) {
      // For first step, use step1 as both prev and current so drop-off is 0
      prev = breakdown.steps?.step1?.count || 0;
    } else {
      prev = breakdown.steps?.[`step${stepIndex}`]?.count || 0;
    }

    const dropOffValue = Math.max(prev - current, 0);
    const dropOffPercent = prev > 0 ? (dropOffValue / prev) * 100 : 0;

    return [
      breakdownName,
      `${dropOffPercent.toFixed(1)}% drop-off`,
      `${formatNumber(dropOffValue)} total times`
    ];
  };

  // Tooltip for light area (drop-off)
  const getLightAreaTooltip = (step, index, prevValue) => {
    const getStepValue = (s) => {
      if (measuring === 'conversion_rate') {
        return s.count || 0;
      } else {
        return s.value || s.count || 0;
      }
    };
    
    const currentValue = getStepValue(step);
    const dropOffValue = Math.max(prevValue - currentValue, 0);
    const dropOffPercent = prevValue > 0 ? ((dropOffValue / prevValue) * 100) : 0;

    return [
      "Overall",
      `${formatPercent(dropOffPercent)} drop-off`,
      `${formatNumber(dropOffValue)} total times`
    ];
  };

  // Tooltip for dark area (converted)
  const getDarkAreaTooltip = (step, index) => {
    const stepMeasuring = measuring;
    const count = step.count || 0;
    const value = step.value || 0;
    const conversion = step.conversion || 0;

    let tooltipLines = [];

    switch (stepMeasuring) {
      case 'conversion_rate':
        tooltipLines = [
          "Overall",
          `${formatPercent(conversion)} converted`,
          `${formatNumber(count)} total times`
        ];
        break;
      case 'unique_users':
        tooltipLines = [
          "Overall",
          `${formatNumber(value || count)} unique users`,
          `${formatNumber(count)} total times`
        ];
        break;
      case 'total_conversions':
        tooltipLines = [
          "Overall",
          `${formatNumber(value)} total conversions`,
          `${formatNumber(count)} total times`
        ];
        break;
      case 'total_sessions':
        tooltipLines = [
          "Overall",
          `${formatNumber(value)} sessions`,
          `${formatNumber(count)} total times`
        ];
        break;
      case 'time_to_convert':
        const timeStr = formatTimeInterval(value || step.timeToConvert);
        tooltipLines = [
          "Overall",
          `${timeStr} average time to convert`,
          `${formatNumber(count)} total times`
        ];
        break;
      default:
        tooltipLines = [
          "Overall",
          `${formatNumber(count)} total times`
        ];
    }

    return tooltipLines;
  };

  // Calculate overall conversion rate (first step to last step)
  const overallConv = overallConversionRate !== null 
    ? overallConversionRate 
    : (steps.length > 1 && steps[0]?.count > 0
      ? ((steps[steps.length - 1]?.count || 0) / steps[0].count) * 100
      : (steps.length === 1 ? 100 : 0));
  
  // Use overall conversion from backend if available
  const finalOverallConv = steps.length > 0 && steps[0]?.overallConversion !== undefined
    ? steps[0].overallConversion
    : overallConv;

  // Y-axis ticks: linear = equal spacing (0, 25, 50, 75, 100); log = base-10 spacing (0.1%, 1%, 10%, 100% or log-spaced in narrow range)
  const getYAxisTicks = () => {
    if (measuring === 'conversion_rate' && yScale === 'log') {
      const logMin = Math.log10(Math.max(axisMinLog, 0.01));
      const logMax = Math.log10(axisMaxLog);
      const span = logMax - logMin;
      // Full range (0.1–100): use powers of 10 so axis clearly looks logarithmic
      if (axisMinLog <= 0.5 && axisMaxLog >= 50) {
        const powerTicks = [0.1, 1, 10, 100].filter((t) => t >= axisMinLog && t <= axisMaxLog);
        return powerTicks.length >= 2 ? powerTicks : [axisMinLog, axisMaxLog];
      }
      // Narrow range: 5 log-spaced ticks (ends = axis min/max) so spacing is visibly unequal
      return Array.from({ length: 5 }, (_, i) => {
        if (i === 0) return axisMinLog;
        if (i === 4) return axisMaxLog;
        const v = Math.pow(10, logMin + (span * i) / 4);
        return Math.round(v * 100) / 100;
      });
    }
    if (measuring === 'conversion_rate' && effectiveHasThresholds && rangeSpan > 0) {
      return [rangeMin, rangeMin + rangeSpan * 0.25, rangeMin + rangeSpan * 0.5, rangeMin + rangeSpan * 0.75, rangeMax];
    }
    if (measuring === "conversion_rate" || measuring === "time_to_convert") {
      return [0, 25, 50, 75, 100];
    } else {
      // For other measuring types (unique_users, total_conversions, total_sessions), show numbers
      const tickCount = 5;
      const ticks = [];
      const maxValue = hasBreakdowns ? maxBreakdownStep1Value : firstStepValue;
      for (let i = 0; i <= tickCount; i++) {
        const value = (maxValue / tickCount) * i;
        ticks.push(Math.round(value));
      }
      return ticks;
    }
  };

  const yAxisTicks = getYAxisTicks();
  // Tick position from top of visible area (0 = bottom, 100 = top)
  const getTickTopPercent = (tick) => {
    if (measuring === 'conversion_rate' && yScale === 'log') {
      const t = Math.max(tick, axisMinLog);
      return 100 - ((Math.log10(t) - Math.log10(axisMinLog)) / logSpan) * 100;
    }
    if (measuring === 'conversion_rate' && effectiveHasThresholds && rangeSpan > 0) {
      return 100 - ((tick - rangeMin) / rangeSpan) * 100;
    }
    return measuring === "conversion_rate" || measuring === "time_to_convert"
      ? 100 - tick
      : 100 - (firstStepValue > 0 ? (tick / firstStepValue) * 100 : 0);
  };

  const containerHeight =
    typeof chartHeight === "number" && chartHeight > 0 ? chartHeight : 280;
  // Linear + thresholds: crop plot to range. Log: full height; axis range is axisMinLog–axisMaxLog.
  const plotHeight = yScale === "log" ? containerHeight : (effectiveHasThresholds && rangeSpan > 0 ? (rangeSpan / 100) * containerHeight : containerHeight);
  const cropOffsetBottom = yScale === "log" ? 0 : (effectiveHasThresholds && rangeSpan > 0 ? (rangeMin / 100) * containerHeight : 0);

  // Handle light area hover (drop-off) - anchor to top-right of container
  const handleLightAreaHover = (index, event) => {
    const containerRef = stepRefs.current[index];
    if (containerRef) {
      const rect = containerRef.getBoundingClientRect();
      setHoveredLightArea({
        stepIndex: index,
        x: rect.right - 12, // Top-right of container
        y: rect.top + 12
      });
    }
  };

  // Handle dark area hover (converted) - anchor to top-right of dark block
  const handleDarkAreaHover = (index, event) => {
    const darkRef = darkAreaRefs.current[index];
    if (darkRef) {
      const rect = darkRef.getBoundingClientRect();
      setHoveredDarkArea({
        stepIndex: index,
        x: rect.right - 12, // Top-right of dark block
        y: rect.top + 12
      });
    }
  };

  // Handle breakdown segment hover
  const handleBreakdownHover = (stepIndex, breakdownIndex, kind, event) => {
    const refKey = `${stepIndex}-${breakdownIndex}`;
    const segmentRef = segmentRefs.current[refKey];
    if (segmentRef) {
      const rect = segmentRef.getBoundingClientRect();
      setHoveredSegment({
        stepIndex,
        breakdownIndex,
        kind,
        x: rect.right - 12,
        y: rect.top + 12
      });
    }
  };

  // Render breakdown mode (multiple bars per step)
  if (hasBreakdowns) {
    return (
      <div className="w-full">
        {/* Breakdown Legend */}
        <div className={boardCompact ? "mb-2 flex flex-wrap items-center gap-2" : "mb-4 flex flex-wrap items-center gap-4"}>
          {breakdowns.map((breakdown, index) => {
            const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';
            const overallConv = breakdown.overallConversion || 0;
            const color = breakdownColors[index % breakdownColors.length];
            
            return (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: color }}
                />
                <span
                  className={boardCompact ? "" : "text-sm"}
                  style={boardCompact ? { color: theme.textPrimary, fontSize: fs(12) } : { color: theme.textPrimary }}
                >
                  {breakdownName}
                </span>
                <span
                  className={boardCompact ? "" : "text-sm"}
                  style={boardCompact ? { color: theme.textMuted, fontSize: fs(12) } : { color: theme.textMuted }}
                >
                  •{" "}
                  {measuring === "time_to_convert" && steps.length >= 2
                    ? steps[steps.length - 1]?.timeToConvert ||
                      formatDurationSeconds(steps[steps.length - 1]?.value)
                    : `${overallConv.toFixed(1)}%`}
                </span>
              </div>
            );
          })}
        </div>

        {/* Funnel Chart with Breakdown Bars: extra bottom padding so step labels clear value badges */}
        <div className={boardCompact ? "relative mb-3 pb-3" : "relative mb-10 pb-10"}>
          {/* Y-Axis Label (beyond the axis, only when leftYVisible) */}
          {leftYVisible && leftYLabel && (
            <div
              className="absolute top-1/2 -rotate-90 origin-center text-xs"
              style={{
                color: theme.textMuted,
                whiteSpace: "nowrap",
                transform: funnelAxis === "right"
                  ? "translateX(100%) translateY(-50%) rotate(90deg)"
                  : "translateX(-100%) translateY(-50%) rotate(-90deg)",
                transformOrigin: "center",
                [funnelAxis === "right" ? "right" : "left"]: "8px",
              }}
            >
              {leftYLabel}
            </div>
          )}
          {/* Y-Axis (only when leftYVisible) */}
          {leftYVisible && (
            <div className={`absolute top-0 bottom-0 ${axisColClass} ${axisEdgeClass}`}>
              {yAxisTicks.map((tick, i) => {
                const tickPosition =
                  measuring === "conversion_rate" || measuring === "time_to_convert"
                    ? tick
                    : maxBreakdownStep1Value > 0
                    ? (tick / maxBreakdownStep1Value) * 100
                    : 0;

                return (
                  <div
                    key={i}
                    className={`absolute text-xs ${funnelAxis === "right" ? "text-left" : "text-right"}`}
                    style={{
                      top: `${100 - tickPosition}%`,
                      color: theme.textMuted,
                      transform: "translateY(-50%)",
                    }}
                  >
                    {measuring === "time_to_convert" ? `${tick}%` : formatYValue(tick)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Grid Lines (only when leftYVisible) */}
          {leftYVisible && (
            <div className={`absolute top-0 bottom-0 ${gridInsetClass}`}>
              {yAxisTicks.map((tick, i) => {
                const tickPosition =
                  measuring === "conversion_rate" || measuring === "time_to_convert"
                    ? tick
                    : maxBreakdownStep1Value > 0
                    ? (tick / maxBreakdownStep1Value) * 100
                    : 0;

                return (
                  <div
                    key={i}
                    className="absolute left-0 right-0 border-t"
                    style={{
                      top: `${100 - tickPosition}%`,
                      borderColor: theme.border,
                      opacity: 0.3,
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Chart Area - Multiple bars per step */}
          <div 
            className="flex items-end"
            style={{ 
              height: `${containerHeight}px`,
              ...chartAxisSideMargin,
            }}
          >
            {steps.map((step, stepIndex) => {
              const stepLabel = step.label || step.eventName || `Step ${stepIndex + 1}`;
              
              return (
                <div
                  key={stepIndex}
                  className="relative flex-1 h-full"
                  style={{
                    borderRight: stepIndex < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  {/* Render bars for each breakdown segment */}
                  <div className="relative w-full h-full flex gap-1">
                    {breakdowns.map((breakdown, breakdownIndex) => {
                      const { lightHeight, darkHeight } = getBreakdownHeights(breakdown, stepIndex);
                      const color = breakdownColors[breakdownIndex % breakdownColors.length];
                      const isHovered = hoveredSegment?.stepIndex === stepIndex && 
                                        hoveredSegment?.breakdownIndex === breakdownIndex;
                      const refKey = `${stepIndex}-${breakdownIndex}`;

                      return (
                        <div
                          key={breakdownIndex}
                          ref={(el) => { segmentRefs.current[refKey] = el; }}
                          className="relative h-full flex-1"
                        >
                          {/* Light background (previous step) - only for step > 0 */}
                          {stepIndex > 0 && (
                            <div 
                              className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                              onMouseEnter={(e) => handleBreakdownHover(stepIndex, breakdownIndex, 'light', e)}
                              onMouseLeave={() => setHoveredSegment(null)}
                              style={{ 
                                height: `${lightHeight}%`,
                                backgroundColor: color,
                                opacity: isHovered && hoveredSegment?.kind === 'light' ? 0.35 : 0.25
                              }}
                            />
                          )}

                          {/* Dark fill (current step) */}
                          <div
                            className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                  onMouseEnter={(e) => handleBreakdownHover(stepIndex, breakdownIndex, 'dark', e)}
                  onMouseLeave={() => {
                    if (drilldown) scheduleClearHover(() => setHoveredSegment(null));
                    else setHoveredSegment(null);
                  }}
                  style={{
                    height: `${darkHeight}%`,
                    background: color,
                    opacity: isHovered && hoveredSegment?.kind === 'dark' ? 0.98 : 1,
                              boxShadow: isHovered && hoveredSegment?.kind === 'dark'
                                ? `0 4px 12px ${color}40`
                                : `0 2px 4px ${color}25`
                            }}
                          />

                          {/* Tooltip */}
                          {isHovered && hoveredSegment?.x !== undefined && hoveredSegment?.y !== undefined && (
                            <div
                              className={`fixed z-50 bg-surface border ${drilldown && hoveredSegment.kind === 'dark' ? 'pointer-events-auto' : 'pointer-events-none'}`}
                              style={{
                                left: `${hoveredSegment.x}px`,
                                top: `${hoveredSegment.y}px`,
                                transform: 'translate(-100%, 0)',
                                padding: '12px',
                                minWidth: '200px',
                                maxWidth: '300px',
                                borderColor: theme.border,
                                borderLeftWidth: '3px',
                                borderLeftColor: color,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                borderRadius: '8px'
                              }}
                              onMouseEnter={drilldown && hoveredSegment.kind === 'dark' ? cancelScheduledHoverClear : undefined}
                              onMouseLeave={
                                drilldown && hoveredSegment.kind === 'dark'
                                  ? () => scheduleClearHover(() => setHoveredSegment(null))
                                  : undefined
                              }
                            >
                              {(hoveredSegment.kind === 'light'
                                ? getBreakdownDropoffTooltip(breakdown, stepIndex)
                                : getBreakdownTooltip(breakdown, stepIndex)
                              ).map((line, i, arr) => (
                                <div 
                                  key={i}
                                  className="text-sm"
                                  style={{ 
                                    color: i === 0 ? theme.textPrimary : theme.textSecondary,
                                    fontWeight: i === 0 ? 600 : 400,
                                    marginBottom: i < arr.length - 1 ? '4px' : '0'
                                  }}
                                >
                                  {line}
                                </div>
                              ))}
                              {drilldown && hoveredSegment.kind === 'dark' && (
                                <div className="mt-2 border-t pt-2" style={{ borderColor: theme.border }}>
                                  <button
                                    type="button"
                                    className="text-sm font-medium hover:underline"
                                    style={{ color: theme.accentChart }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      let bv = Array.isArray(breakdown.breakdownValues)
                                        ? breakdown.breakdownValues
                                        : null;
                                      if (
                                        (!bv || bv.length === 0) &&
                                        typeof breakdown.breakdownKey === "string" &&
                                        breakdown.breakdownKey.trim()
                                      ) {
                                        bv = breakdown.breakdownKey
                                          .split(/\s*\/\s*/)
                                          .map((s) => s.trim())
                                          .filter(Boolean);
                                      }
                                      onRequestStepUsers({ stepIndex, breakdownValues: bv });
                                    }}
                                  >
                                    See users
                                  </button>
                                </div>
                              )}
                              <div
                                className="absolute"
                                style={{
                                  top: '100%',
                                  right: '12px',
                                  transform: 'translateY(-50%) rotate(45deg)',
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: 'white',
                                  borderRight: `1px solid ${theme.border}`,
                                  borderBottom: `1px solid ${theme.border}`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Step label at bottom (X-Axis) */}
                  {xAxisVisible && (
                    <div className="absolute -bottom-8 left-0 right-0 text-center">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: theme.textPrimary,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}
                      >
                        <span style={{ color: theme.textMuted, marginRight: '4px' }}>
                          {stepIndex + 1}
                        </span>
                        <span style={{ color: theme.textPrimary }}>
                          {stepLabel}
                        </span>
                        {renderStepKindChip(step)}
                      </span>
                    </div>
                  )}
                  {/* X-Axis Label (if provided) */}
                  {xAxisVisible && xAxisLabel && stepIndex === steps.length - 1 && (
                    <div className="absolute -bottom-12 left-0 right-0 text-center">
                      <span className="text-xs" style={{ color: theme.textMuted }}>
                        {xAxisLabel}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Original non-breakdown rendering
  return (
    <div className="w-full">
      {/* Overall Conversion Label - Centered */}
      <div className={boardCompact ? "mb-2 flex justify-center" : "mb-4 flex justify-center"}>
        <span
          className={boardCompact ? "font-medium" : "text-sm font-medium"}
          style={boardCompact ? { color: theme.textPrimary, fontSize: fs(12) } : { color: theme.textPrimary }}
        >
          {measuring === "time_to_convert" && steps.length >= 2
            ? `Overall • ${
                steps[steps.length - 1]?.timeToConvert ||
                formatDurationSeconds(steps[steps.length - 1]?.value)
              }`
            : `Overall • ${finalOverallConv.toFixed(1)}%`}
        </span>
      </div>

      {/* Funnel Chart - Connected Step Containers: extra bottom padding for step labels vs. badges */}
      <div className={boardCompact ? "relative mb-3 pb-3" : "relative mb-10 pb-10"}>
          {/* Y-Axis Label (beyond the axis, only when leftYVisible) */}
          {leftYVisible && leftYLabel && (
            <div
              className="absolute top-1/2 -rotate-90 origin-center text-xs"
              style={{
                color: theme.textMuted,
                whiteSpace: "nowrap",
                transform: funnelAxis === "right"
                  ? "translateX(100%) translateY(-50%) rotate(90deg)"
                  : "translateX(-100%) translateY(-50%) rotate(-90deg)",
                transformOrigin: "center",
                [funnelAxis === "right" ? "right" : "left"]: "8px",
              }}
            >
              {leftYLabel}
            </div>
          )}
        {/* Y-Axis with ticks (only when leftYVisible) */}
        {leftYVisible && (
          <div className={`absolute top-0 bottom-0 ${axisColClass} ${axisEdgeClass}`}>
            {yAxisTicks.map((tick, i) => {
              // For conversion_rate, tick is already a percentage (0-100)
              // For other types, calculate position based on firstStepValue
              const tickPosition =
                measuring === "conversion_rate" || measuring === "time_to_convert"
                  ? tick
                  : firstStepValue > 0
                  ? (tick / firstStepValue) * 100
                  : 0;

              return (
                <div
                  key={i}
                  className={`absolute text-xs ${funnelAxis === "right" ? "text-left" : "text-right"}`}
                  style={{
                    top: `${100 - tickPosition}%`,
                    color: theme.textMuted,
                    transform: "translateY(-50%)",
                  }}
                >
                  {measuring === "time_to_convert" ? `${tick}%` : formatYValue(tick)}
                </div>
              );
            })}
          </div>
        )}

        {/* Grid Lines (only when leftYVisible) */}
        {leftYVisible && (
          <div className={`absolute top-0 bottom-0 ${gridInsetClass}`}>
            {yAxisTicks.map((tick, i) => {
              // For conversion_rate, tick is already a percentage (0-100)
              // For other types, calculate position based on firstStepValue
              const tickPosition =
                measuring === "conversion_rate" || measuring === "time_to_convert"
                  ? tick
                  : firstStepValue > 0
                  ? (tick / firstStepValue) * 100
                  : 0;

              return (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-t"
                  style={{
                    top: `${100 - tickPosition}%`,
                    borderColor: theme.border,
                    opacity: 0.3,
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Chart Area - Connected Step Containers */}
        <div 
          className="flex items-end"
          style={{ 
            height: `${containerHeight}px`,
            ...chartAxisSideMargin,
          }}
        >
          {steps.map((step, index) => {
            const { lightHeight, darkHeight, prevValue } = getHeights(step, index);
            const display = getDisplayValue(step, measuring);
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;
            const isLightHovered = hoveredLightArea?.stepIndex === index;
            const isDarkHovered = hoveredDarkArea?.stepIndex === index;

            return (
              <div
                key={step.id || index}
                ref={(el) => { stepRefs.current[index] = el; }}
                className="relative flex-1 h-full"
                style={{
                  borderRight: isLast ? 'none' : `1px solid ${theme.border}`,
                  borderLeft: isFirst ? 'none' : 'none'
                }}
              >
                {/* Light Background (Previous Step Position) - hoverable only for steps > 0 */}
                {index > 0 && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                    onMouseEnter={(e) => handleLightAreaHover(index, e)}
                    onMouseLeave={() => setHoveredLightArea(null)}
                    style={{ 
                      height: `${lightHeight}%`,
                      backgroundColor: isLightHovered
                        ? hexToRgba(funnelBarColor, 0.35)
                        : hexToRgba(funnelBarColor, 0.25),
                      opacity: isLightHovered ? 0.5 : 0.4,
                      borderRadius: isFirst && lightHeight === 100 ? '4px 0 0 4px' : 
                                 isLast && lightHeight === 100 ? '0 4px 4px 0' :
                                 isFirst ? '4px 0 0 0' :
                                 isLast ? '0 4px 0 0' : '0'
                    }}
                  />
                )}

                {/* Dark Fill (Current Step Position) */}
                <div
                  ref={(el) => { darkAreaRefs.current[index] = el; }}
                  className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                  onMouseEnter={(e) => handleDarkAreaHover(index, e)}
                  onMouseLeave={() => {
                    if (drilldown) scheduleClearHover(() => setHoveredDarkArea(null));
                    else setHoveredDarkArea(null);
                  }}
                  style={{
                    height: `${darkHeight}%`,
                    background: funnelBarColor,
                    opacity: isDarkHovered ? 0.98 : 1,
                    borderRadius: isFirst && darkHeight === 100 ? '4px 0 0 4px' : 
                                 isLast && darkHeight === 100 ? '0 4px 4px 0' :
                                 isFirst ? '4px 0 0 0' :
                                 isLast ? '0 4px 0 0' : '0',
                    boxShadow: isDarkHovered
                      ? `0 4px 12px ${hexToRgba(funnelBarColor, 0.4)}`
                      : `0 2px 4px ${hexToRgba(funnelBarColor, 0.25)}`
                  }}
                >
                  {/* Tag centered on top edge of bar (half above / half below) */}
                  {darkHeight > 0 && (
                    <div 
                      className="absolute left-1/2 z-[1]"
                      style={{ 
                        top: 0,
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none'
                      }}
                    >
                      <div
                        className="bg-surface rounded px-2 py-1 text-center shadow-sm"
                        style={{
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                          minWidth: '60px'
                        }}
                      >
                        {/* Percentage */}
                        <div 
                          className="text-xs font-medium leading-tight"
                          style={{ 
                            color: 'var(--text-primary)',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                        >
                          {display.percentage}
                        </div>
                        {/* Count */}
                        <div 
                          className="text-xs font-medium leading-tight"
                          style={{ 
                            color: 'var(--text-primary)',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                        >
                          {display.count}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step Number and Event Name at Bottom (X-Axis) */}
                {xAxisVisible && (
                  <div className="absolute -bottom-8 left-0 right-0 text-center">
                    <span 
                      className="text-sm"
                      style={{ 
                        color: theme.textPrimary,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                      }}
                    >
                      <span style={{ color: theme.textMuted, marginRight: '4px' }}>
                        {index + 1}
                      </span>
                      <span style={{ color: theme.textPrimary }}>
                        {step.label || step.eventName || `Step ${index + 1}`}
                      </span>
                      {renderStepKindChip(step)}
                    </span>
                  </div>
                )}
                {/* X-Axis Label (if provided) */}
                {xAxisVisible && xAxisLabel && index === steps.length - 1 && (
                  <div className="absolute -bottom-12 left-0 right-0 text-center">
                    <span className="text-xs" style={{ color: theme.textMuted }}>
                      {xAxisLabel}
                    </span>
                  </div>
                )}

                {/* Tooltip for Light Area (drop-off) - rendered outside container with fixed positioning */}
                {isLightHovered && index > 0 && hoveredLightArea.x !== undefined && hoveredLightArea.y !== undefined && (
                  <div
                    className="fixed z-50 bg-surface border pointer-events-none"
                    style={{
                      left: `${hoveredLightArea.x}px`,
                      top: `${hoveredLightArea.y}px`,
                      transform: 'translate(-100%, 0)',
                      padding: '12px',
                      minWidth: '200px',
                      maxWidth: '300px',
                      borderColor: theme.border,
                      borderLeftWidth: '3px',
                      borderLeftColor: funnelBarColor,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderRadius: '8px',
                    }}
                  >
                    {getLightAreaTooltip(step, index, prevValue).map((line, i) => (
                      <div 
                        key={i}
                        className="text-sm"
                        style={{ 
                          color: i === 0 ? theme.textPrimary : theme.textSecondary,
                          fontWeight: i === 0 ? 600 : 400,
                          marginBottom: i < getLightAreaTooltip(step, index, prevValue).length - 1 ? '4px' : '0'
                        }}
                      >
                        {line}
                      </div>
                    ))}
                    {/* Tooltip Arrow - pointing down and right */}
                    <div
                      className="absolute"
                      style={{
                        top: '100%',
                        right: '12px',
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderRight: `1px solid ${theme.border}`,
                        borderBottom: `1px solid ${theme.border}`
                      }}
                    />
                  </div>
                )}

                {/* Tooltip for Dark Area (converted) - rendered outside container with fixed positioning */}
                {isDarkHovered && hoveredDarkArea.x !== undefined && hoveredDarkArea.y !== undefined && (
                  <div
                    className={`fixed z-50 bg-surface border ${drilldown ? 'pointer-events-auto' : 'pointer-events-none'}`}
                    style={{
                      left: `${hoveredDarkArea.x}px`,
                      top: `${hoveredDarkArea.y}px`,
                      transform: 'translate(-100%, 0)',
                      padding: '12px',
                      minWidth: '200px',
                      maxWidth: '300px',
                      borderColor: theme.border,
                      borderLeftWidth: '3px',
                      borderLeftColor: funnelBarColor,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderRadius: '8px',
                    }}
                    onMouseEnter={drilldown ? cancelScheduledHoverClear : undefined}
                    onMouseLeave={drilldown ? () => scheduleClearHover(() => setHoveredDarkArea(null)) : undefined}
                  >
                    {getDarkAreaTooltip(step, index).map((line, i) => (
                      <div 
                        key={i}
                        className="text-sm"
                        style={{ 
                          color: i === 0 ? theme.textPrimary : theme.textSecondary,
                          fontWeight: i === 0 ? 600 : 400,
                          marginBottom: i < getDarkAreaTooltip(step, index).length - 1 ? '4px' : '0'
                        }}
                      >
                        {line}
                      </div>
                    ))}
                    {drilldown && (
                      <div className="mt-2 border-t pt-2" style={{ borderColor: theme.border }}>
                        <button
                          type="button"
                          className="text-sm font-medium hover:underline"
                          style={{ color: theme.accentChart }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestStepUsers({ stepIndex: index, breakdownValues: null });
                          }}
                        >
                          See users
                        </button>
                      </div>
                    )}
                    {/* Tooltip Arrow - pointing down and right */}
                    <div
                      className="absolute"
                      style={{
                        top: '100%',
                        right: '12px',
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderRight: `1px solid ${theme.border}`,
                        borderBottom: `1px solid ${theme.border}`
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
